/* eslint-disable react/jsx-no-bind, react-native/no-inline-styles, @typescript-eslint/no-unused-vars, @stylistic/max-statements-per-line */
import React from 'react';
import { connect } from 'react-redux';
import { isMobileBrowser } from '../../base/environment/utils';
import { translate } from '../../base/i18n/functions';
import Icon from '../../base/icons/components/Icon';
import { IconWarning } from '../../base/icons/svg';
import getUnsafeRoomText from '../../base/util/getUnsafeRoomText.web';
import CalendarList from '../../calendar-sync/components/CalendarList.web';
import RecentList from '../../recent-list/components/RecentList.web';
import { updateSettings } from '../../base/settings/actions';
import { AbstractWelcomePage, _mapStateToProps } from './AbstractWelcomePage';
import HostMeetingView from './HostMeetingView.web';
import JoinMeetingView from './JoinMeetingView.web';
import Tabs from './Tabs';
/**
 * The pattern used to validate room name.
 *
 * @type {string}
 */
export const ROOM_NAME_VALIDATE_PATTERN_STR = '^[^?&:\u0022\u0027%#]+$';
/**
 * The Web container rendering the welcome page.
 *
 * @augments AbstractWelcomePage
 */
class WelcomePage extends AbstractWelcomePage {
    _additionalContentRef;
    _additionalToolbarContentRef;
    _additionalCardRef;
    _roomInputRef;
    _additionalCardTemplate;
    _additionalContentTemplate;
    _additionalToolbarContentTemplate;
    _titleHasNotAllowCharacter;
    /**
     * Default values for {@code WelcomePage} component's properties.
     *
     * @static
     */
    static defaultProps = {
        _room: ''
    };
    /**
     * Initializes a new WelcomePage instance.
     *
     * @param {Object} props - The read-only properties with which the new
     * instance is to be initialized.
     */
    constructor(props) {
        super(props);
        this.state = {
            ...this.state,
            showJoinPage: false,
            generateRoomNames: interfaceConfig.GENERATE_ROOMNAMES_ON_WELCOME_PAGE
        };
        /**
      * Used To display a warning massage if the title input has no allow character.
      *
      * @private
      * @type {boolean}
      */
        this._titleHasNotAllowCharacter = false;
        /**
         * The HTML Element used as the container for additional content. Used
         * for directly appending the additional content template to the dom.
         *
         * @private
         * @type {HTMLTemplateElement|null}
         */
        this._additionalContentRef = null;
        this._roomInputRef = null;
        /**
         * The HTML Element used as the container for additional toolbar content. Used
         * for directly appending the additional content template to the dom.
         *
         * @private
         * @type {HTMLTemplateElement|null}
         */
        this._additionalToolbarContentRef = null;
        this._additionalCardRef = null;
        /**
         * The template to use as the additional card displayed near the main one.
         *
         * @private
         * @type {HTMLTemplateElement|null}
         */
        this._additionalCardTemplate = document.getElementById('welcome-page-additional-card-template');
        /**
         * The template to use as the main content for the welcome page. If
         * not found then only the welcome page head will display.
         *
         * @private
         * @type {HTMLTemplateElement|null}
         */
        this._additionalContentTemplate = document.getElementById('welcome-page-additional-content-template');
        /**
         * The template to use as the additional content for the welcome page header toolbar.
         * If not found then only the settings icon will be displayed.
         *
         * @private
         * @type {HTMLTemplateElement|null}
         */
        this._additionalToolbarContentTemplate = document.getElementById('settings-toolbar-additional-content-template');
        // Bind event handlers so they are only bound once per instance.
        this._onFormSubmit = this._onFormSubmit.bind(this);
        this._onRoomChange = this._onRoomChange.bind(this);
        this._setAdditionalCardRef = this._setAdditionalCardRef.bind(this);
        this._setAdditionalContentRef
            = this._setAdditionalContentRef.bind(this);
        this._setRoomInputRef = this._setRoomInputRef.bind(this);
        this._setAdditionalToolbarContentRef
            = this._setAdditionalToolbarContentRef.bind(this);
        this._renderFooter = this._renderFooter.bind(this);
    }
    /**
     * Implements React's {@link Component#componentDidMount()}. Invoked
     * immediately after this component is mounted.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentDidMount() {
        super.componentDidMount();
        document.body.classList.add('welcome-page');
        document.title = 'Fresh Call';
        if (this.state.generateRoomNames) {
            this._updateRoomName();
        }
        if (this._shouldShowAdditionalContent()) {
            this._additionalContentRef?.appendChild(this._additionalContentTemplate?.content.cloneNode(true));
        }
        if (this._shouldShowAdditionalToolbarContent()) {
            this._additionalToolbarContentRef?.appendChild(this._additionalToolbarContentTemplate?.content.cloneNode(true));
        }
        if (this._shouldShowAdditionalCard()) {
            this._additionalCardRef?.appendChild(this._additionalCardTemplate?.content.cloneNode(true));
        }
    }
    /**
     * Removes the classname used for custom styling of the welcome page.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentWillUnmount() {
        super.componentWillUnmount();
        document.body.classList.remove('welcome-page');
    }
    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement|null}
     */
    render() {
        const { _moderatedRoomServiceUrl, t } = this.props;
        const { DEFAULT_WELCOME_PAGE_LOGO_URL, DISPLAY_WELCOME_FOOTER } = interfaceConfig;
        const showAdditionalCard = this._shouldShowAdditionalCard();
        const showAdditionalContent = this._shouldShowAdditionalContent();
        const showAdditionalToolbarContent = this._shouldShowAdditionalToolbarContent();
        const contentClassName = showAdditionalContent ? 'with-content' : 'without-content';
        const footerClassName = DISPLAY_WELCOME_FOOTER ? 'with-footer' : 'without-footer';
        return (React.createElement("div", { className: 'custom-welcome-page' },
            React.createElement("nav", { className: 'navbar' },
                React.createElement("div", { className: 'nav-left' },
                    React.createElement("div", { className: 'logo-icon' },
                        React.createElement("svg", { fill: 'white', height: '24', viewBox: '0 0 24 24', width: '24' },
                            React.createElement("path", { d: 'M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z' }))),
                    "Fresh Call"),
                React.createElement("div", { className: 'nav-middle' },
                    React.createElement("a", { href: '#' }, "Resources")),
                React.createElement("div", { className: 'nav-right' },
                    React.createElement("a", { className: `btn-text ${this.state.showJoinPage ? 'active' : ''}`, href: '#', onClick: e => {
                            e.preventDefault();
                            this.setState({ showJoinPage: true, showHostPage: false });
                        } }, "Join a meeting"),
                    React.createElement("a", { className: `btn-text ${this.state.showHostPage ? 'active' : ''}`, href: '#', onClick: e => {
                            e.preventDefault();
                            this.setState({ showHostPage: true, showJoinPage: false });
                        } }, "Host a meeting"),
                    React.createElement("a", { className: 'btn-text', href: '#' }, "Sign in"),
                    React.createElement("button", { className: 'btn-primary-small' }, "Get started free"))),
            this.state.showHostPage ? (React.createElement(HostMeetingView, { generatedRoomName: this.state.generatedRoomName, onHost: (r, options) => {
                    // Apply the toggles to local settings before joining
                    this.props.dispatch(updateSettings({
                        startWithAudioMuted: !options.micOn,
                        startWithVideoMuted: !options.videoOn,
                        fcHostWaitingRoom: options.waitingRoom,
                        fcHostRequirePasscode: options.requirePasscode,
                        fcHostLiveTranscription: options.liveTranscription
                    }));
                    this.setState({ room: r + '#config.prejoinConfig.enabled=false' }, () => this._onJoin());
                } })) : this.state.showJoinPage ? (React.createElement(JoinMeetingView, { onJoin: r => {
                    this.setState({ room: r + '#config.prejoinConfig.enabled=false' }, () => this._onJoin());
                } })) : (React.createElement("div", { className: 'hero-section' },
                React.createElement("div", { className: 'hero-content' },
                    React.createElement("div", { className: 'badge' },
                        React.createElement("div", { className: 'dot' }),
                        "Video meetings for everyone"),
                    React.createElement("h1", null,
                        "Connect, collaborate",
                        React.createElement("br", null),
                        "and get more done",
                        React.createElement("br", null),
                        "with ",
                        React.createElement("span", null, "Fresh Call")),
                    React.createElement("p", { className: 'subtitle' }, "Secure, reliable and high quality video meetings for teams, businesses and individuals."),
                    React.createElement("div", { className: 'actions' },
                        React.createElement("button", { className: 'btn-primary', onClick: () => {
                                this.setState({
                                    showHostPage: true,
                                    room: this.state.room || this.state.generatedRoomName
                                });
                            } },
                            "Start a meeting",
                            React.createElement("svg", { fill: 'white', height: '20', viewBox: '0 0 24 24', width: '20' },
                                React.createElement("path", { d: 'M5 12h14M12 5l7 7-7 7', stroke: 'white', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '2' }))),
                        React.createElement("div", { className: 'join-input-group' },
                            React.createElement("form", { onSubmit: (e) => {
                                    e.preventDefault();
                                    if (!this._roomInputRef || this._roomInputRef.reportValidity()) {
                                        this.setState({ showJoinPage: true });
                                    }
                                }, className: 'join-meeting-form' },
                                React.createElement("input", { "aria-disabled": 'false', "aria-label": t('welcomepage.accessibilityLabel.roomname'), autoFocus: true, id: 'enter_room_field', onChange: this._onRoomChange, pattern: ROOM_NAME_VALIDATE_PATTERN_STR, placeholder: 'Enter meeting code', ref: this._setRoomInputRef, type: 'text', value: this.state.room }),
                                React.createElement("button", { className: 'btn-secondary', onClick: () => {
                                        if (!this._roomInputRef || this._roomInputRef.reportValidity()) {
                                            this.setState({ showJoinPage: true });
                                        }
                                    }, type: 'button' }, "Join a meeting")))),
                    React.createElement("div", { className: 'perks' },
                        React.createElement("span", null, "No account required"),
                        React.createElement("span", null, "Free for everyone"),
                        React.createElement("span", null, "Works on any device"))),
                React.createElement("div", { className: 'hero-image' },
                    React.createElement("img", { alt: 'Fresh Call on multiple devices', src: './images/homescreen.png' })))),
            React.createElement("div", { className: 'features-row' },
                React.createElement("div", { className: 'feature-item' },
                    React.createElement("div", { className: 'icon' },
                        React.createElement("svg", { viewBox: '0 0 24 24' },
                            React.createElement("path", { d: 'M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z' }))),
                    React.createElement("h3", null, "HD Video & Audio"),
                    React.createElement("p", null, "Crystal clear meetings")),
                React.createElement("div", { className: 'feature-item' },
                    React.createElement("div", { className: 'icon' },
                        React.createElement("svg", { viewBox: '0 0 24 24' },
                            React.createElement("path", { d: 'M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z' }))),
                    React.createElement("h3", null, "Secure by default"),
                    React.createElement("p", null, "End to end encryption")),
                React.createElement("div", { className: 'feature-item' },
                    React.createElement("div", { className: 'icon' },
                        React.createElement("svg", { viewBox: '0 0 24 24' },
                            React.createElement("path", { d: 'M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z' }))),
                    React.createElement("h3", null, "Meet with anyone"),
                    React.createElement("p", null, "No account required")),
                React.createElement("div", { className: 'feature-item' },
                    React.createElement("div", { className: 'icon' },
                        React.createElement("svg", { viewBox: '0 0 24 24' },
                            React.createElement("path", { d: 'M4 6h18V4H4c-1.1 0-2 .9-2 2v11H0v3h14v-3H4V6zm19 2h-6c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h6c.55 0 1-.45 1-1V9c0-.55-.45-1-1-1zm-1 9h-4v-7h4v7z' }))),
                    React.createElement("h3", null, "Works everywhere"),
                    React.createElement("p", null, "Web, iOS, Android, Desktop")))));
    }
    /**
     * Renders the insecure room name warning.
     *
     * @inheritdoc
     */
    _doRenderInsecureRoomNameWarning() {
        return (React.createElement("div", { className: 'insecure-room-name-warning' },
            React.createElement(Icon, { src: IconWarning }),
            React.createElement("span", null, getUnsafeRoomText(this.props.t, 'welcome'))));
    }
    /**
     * Prevents submission of the form and delegates join logic.
     *
     * @param {Event} event - The HTML Event which details the form submission.
     * @private
     * @returns {void}
     */
    _onFormSubmit(event) {
        event.preventDefault();
        if (!this._roomInputRef || this._roomInputRef.reportValidity()) {
            this._onJoin();
        }
    }
    /**
     * Overrides the super to account for the differences in the argument types
     * provided by HTML and React Native text inputs.
     *
     * @inheritdoc
     * @override
     * @param {Event} event - The (HTML) Event which details the change such as
     * the EventTarget.
     * @protected
     */
    // @ts-ignore
    // eslint-disable-next-line require-jsdoc
    _onRoomChange(event) {
        const specialCharacters = ['?', '&', ':', '\'', '"', '%', '#', '.'];
        this._titleHasNotAllowCharacter = specialCharacters.some(char => event.target.value.includes(char));
        super._onRoomChange(event.target.value);
    }
    /**
     * Renders the footer.
     *
     * @returns {ReactElement}
     */
    _renderFooter() {
        const { t, _deeplinkingCfg: { ios = { downloadLink: undefined }, android = {
            fDroidUrl: undefined,
            downloadLink: undefined
        } } } = this.props;
        const { downloadLink: iosDownloadLink } = ios;
        const { fDroidUrl, downloadLink: androidDownloadLink } = android;
        return (React.createElement("footer", { className: 'welcome-footer' },
            React.createElement("div", { className: 'welcome-footer-centered' },
                React.createElement("div", { className: 'welcome-footer-padded' },
                    React.createElement("div", { className: 'welcome-footer-row-block welcome-footer--row-1' },
                        React.createElement("div", { className: 'welcome-footer-row-1-text' }, t('welcomepage.jitsiOnMobile')),
                        React.createElement("a", { className: 'welcome-badge', href: iosDownloadLink, rel: 'noopener noreferrer', target: '_blank' },
                            React.createElement("img", { alt: t('welcomepage.mobileDownLoadLinkIos'), src: './images/app-store-badge.png' })),
                        React.createElement("a", { className: 'welcome-badge', href: androidDownloadLink, rel: 'noopener noreferrer', target: '_blank' },
                            React.createElement("img", { alt: t('welcomepage.mobileDownLoadLinkAndroid'), src: './images/google-play-badge.png' })),
                        React.createElement("a", { className: 'welcome-badge', href: fDroidUrl, rel: 'noopener noreferrer', target: '_blank' },
                            React.createElement("img", { alt: t('welcomepage.mobileDownLoadLinkFDroid'), src: './images/f-droid-badge.png' })))))));
    }
    /**
     * Renders tabs to show previous meetings and upcoming calendar events. The
     * tabs are purposefully hidden on mobile browsers.
     *
     * @returns {ReactElement|null}
     */
    _renderTabs() {
        if (isMobileBrowser()) {
            return null;
        }
        const { _calendarEnabled, _recentListEnabled, t } = this.props;
        const tabs = [];
        if (_calendarEnabled) {
            tabs.push({
                id: 'calendar',
                label: t('welcomepage.upcomingMeetings'),
                content: React.createElement(CalendarList, null)
            });
        }
        if (_recentListEnabled) {
            tabs.push({
                id: 'recent',
                label: t('welcomepage.recentMeetings'),
                content: React.createElement(RecentList, null)
            });
        }
        if (tabs.length === 0) {
            return null;
        }
        return (React.createElement(Tabs, { accessibilityLabel: t('welcomepage.meetingsAccessibilityLabel'), tabs: tabs }));
    }
    /**
     * Sets the internal reference to the HTMLDivElement used to hold the
     * additional card shown near the tabs card.
     *
     * @param {HTMLDivElement} el - The HTMLElement for the div that is the root
     * of the welcome page content.
     * @private
     * @returns {void}
     */
    _setAdditionalCardRef(el) {
        this._additionalCardRef = el;
    }
    /**
     * Sets the internal reference to the HTMLDivElement used to hold the
     * welcome page content.
     *
     * @param {HTMLDivElement} el - The HTMLElement for the div that is the root
     * of the welcome page content.
     * @private
     * @returns {void}
     */
    _setAdditionalContentRef(el) {
        this._additionalContentRef = el;
    }
    /**
     * Sets the internal reference to the HTMLDivElement used to hold the
     * toolbar additional content.
     *
     * @param {HTMLDivElement} el - The HTMLElement for the div that is the root
     * of the additional toolbar content.
     * @private
     * @returns {void}
     */
    _setAdditionalToolbarContentRef(el) {
        this._additionalToolbarContentRef = el;
    }
    /**
     * Sets the internal reference to the HTMLInputElement used to hold the
     * welcome page input room element.
     *
     * @param {HTMLInputElement} el - The HTMLElement for the input of the room name on the welcome page.
     * @private
     * @returns {void}
     */
    _setRoomInputRef(el) {
        this._roomInputRef = el;
    }
    /**
     * Returns whether or not an additional card should be displayed near the tabs.
     *
     * @private
     * @returns {boolean}
     */
    _shouldShowAdditionalCard() {
        return interfaceConfig.DISPLAY_WELCOME_PAGE_ADDITIONAL_CARD
            && this._additionalCardTemplate?.content
            && this._additionalCardTemplate?.innerHTML?.trim();
    }
    /**
     * Returns whether or not additional content should be displayed below
     * the welcome page's header for entering a room name.
     *
     * @private
     * @returns {boolean}
     */
    _shouldShowAdditionalContent() {
        return interfaceConfig.DISPLAY_WELCOME_PAGE_CONTENT
            && this._additionalContentTemplate?.content
            && this._additionalContentTemplate?.innerHTML?.trim();
    }
    /**
     * Returns whether or not additional content should be displayed inside
     * the header toolbar.
     *
     * @private
     * @returns {boolean}
     */
    _shouldShowAdditionalToolbarContent() {
        return interfaceConfig.DISPLAY_WELCOME_PAGE_TOOLBAR_ADDITIONAL_CONTENT
            && this._additionalToolbarContentTemplate?.content
            && this._additionalToolbarContentTemplate?.innerHTML.trim();
    }
}
export default translate(connect(_mapStateToProps)(WelcomePage));
